import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

const LOCATION_CACHE_KEY = 'sportrank_user_location';
const LOCATION_CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

interface CachedLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  locationName: string | null;
  timestamp: number;
}

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  error: string | null;
  loading: boolean;
  locationName: string | null;
}

function getCachedLocation(): CachedLocation | null {
  try {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedLocation = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;
    
    // Return cached location if still valid
    if (age < LOCATION_CACHE_MAX_AGE) {
      return parsed;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(LOCATION_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

function setCachedLocation(location: Omit<CachedLocation, 'timestamp'>): void {
  try {
    const cached: CachedLocation = {
      ...location,
      timestamp: Date.now(),
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

export const useGeolocation = () => {
  // Initialize with cached location if available
  const cachedLocation = getCachedLocation();
  
  const [state, setState] = useState<GeolocationState>({
    latitude: cachedLocation?.latitude ?? null,
    longitude: cachedLocation?.longitude ?? null,
    accuracy: cachedLocation?.accuracy ?? null,
    error: null,
    loading: false,
    locationName: cachedLocation?.locationName ?? null,
  });

  const getLocationName = async (lat: number, lng: number): Promise<string | null> => {
    try {
      // Using OpenStreetMap Nominatim for reverse geocoding (free, no API key needed)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'vi',
          },
        }
      );
      const data = await response.json();
      
      if (data.address) {
        // Extract meaningful location parts
        const parts = [];
        if (data.address.suburb) parts.push(data.address.suburb);
        if (data.address.city || data.address.town || data.address.village) {
          parts.push(data.address.city || data.address.town || data.address.village);
        }
        if (data.address.state) parts.push(data.address.state);
        
        return parts.length > 0 ? parts.join(', ') : data.display_name?.split(',').slice(0, 3).join(',');
      }
      return null;
    } catch (error) {
      console.error('Error getting location name:', error);
      return null;
    }
  };

  const requestLocation = useCallback((forceRefresh = false) => {
    // If we have a valid cached location and not forcing refresh, use it
    if (!forceRefresh) {
      const cached = getCachedLocation();
      if (cached) {
        setState({
          latitude: cached.latitude,
          longitude: cached.longitude,
          accuracy: cached.accuracy,
          error: null,
          loading: false,
          locationName: cached.locationName,
        });
        return;
      }
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    // Native platform: use Capacitor Geolocation plugin (better UX, native permission dialog)
    if (Capacitor.isNativePlatform()) {
      (async () => {
        try {
          // Request permission first on native
          const perm = await Geolocation.checkPermissions();
          if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
            const requested = await Geolocation.requestPermissions({ permissions: ['location'] });
            if (requested.location !== 'granted' && requested.coarseLocation !== 'granted') {
              setState(prev => ({
                ...prev,
                error: 'Bạn đã từ chối quyền truy cập vị trí. Vui lòng bật định vị trong cài đặt thiết bị.',
                loading: false,
              }));
              return;
            }
          }

          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: LOCATION_CACHE_MAX_AGE,
          });
          const { latitude, longitude, accuracy } = position.coords;
          const locationName = await getLocationName(latitude, longitude);
          setCachedLocation({ latitude, longitude, accuracy, locationName });
          setState({
            latitude,
            longitude,
            accuracy,
            error: null,
            loading: false,
            locationName,
          });
        } catch (err) {
          console.error('Native geolocation error:', err);
          setState(prev => ({
            ...prev,
            error: 'Không thể lấy vị trí. Vui lòng kiểm tra GPS của bạn.',
            loading: false,
          }));
        }
      })();
      return;
    }

    // Web fallback
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: 'Trình duyệt không hỗ trợ định vị',
        loading: false,
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const locationName = await getLocationName(latitude, longitude);
        setCachedLocation({ latitude, longitude, accuracy, locationName });
        setState({
          latitude,
          longitude,
          accuracy,
          error: null,
          loading: false,
          locationName,
        });
      },
      (error) => {
        let errorMessage = 'Không thể lấy vị trí của bạn';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Bạn đã từ chối quyền truy cập vị trí. Vui lòng bật định vị trong cài đặt trình duyệt.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Không thể xác định vị trí. Vui lòng kiểm tra GPS của bạn.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Hết thời gian chờ lấy vị trí. Vui lòng thử lại.';
            break;
        }
        setState(prev => ({
          ...prev,
          error: errorMessage,
          loading: false,
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: LOCATION_CACHE_MAX_AGE,
      }
    );
  }, []);

  const clearLocation = useCallback(() => {
    localStorage.removeItem(LOCATION_CACHE_KEY);
    setState({
      latitude: null,
      longitude: null,
      accuracy: null,
      error: null,
      loading: false,
      locationName: null,
    });
  }, []);

  // Auto-request location on mount if we have cached location
  useEffect(() => {
    if (cachedLocation) {
      // We already have cached location, optionally refresh in background
      // Only refresh if cache is more than half expired
      const age = Date.now() - cachedLocation.timestamp;
      if (age > LOCATION_CACHE_MAX_AGE / 2) {
        requestLocation(true);
      }
    }
  }, []); // Run once on mount

  return {
    ...state,
    requestLocation,
    clearLocation,
    hasLocation: state.latitude !== null && state.longitude !== null,
  };
};
